import { countries } from '../constants/countries';
import adminDivisions from '../constants/country-admin-divisions';

const { AU_STATES, US_STATES } = adminDivisions;

export const getFullCountryName = value => {
  if (!value) return null;

  const lowerValue = value.toLowerCase();

  // Alpha-2 code match
  if (value.length === 2) {
    const match = countries.find(c => c.alpha2?.toLowerCase() === lowerValue);
    return match?.value ?? value;
  }

  // Full name match
  if (value.length > 3) {
    const match = countries.find(c => c.value.toLowerCase() === lowerValue);
    return match?.value ?? value;
  }

  // Alpha-3 code match
  if (value.length === 3) {
    const match = countries.find(c => c.alpha3?.toLowerCase() === lowerValue);
    return match?.value ?? value;
  }

  return value;
};

export const getFullStateName = (stateCode, countryCode) => {
  if (!stateCode) return null;

  const lowerStateCode = stateCode.toLowerCase();
  const lowerCountryCode = countryCode?.toLowerCase();

  // Try Australian states first
  if (lowerCountryCode === 'au' || !countryCode) {
    const auMatch = AU_STATES.find(state => state.value.toLowerCase() === lowerStateCode);
    if (auMatch) return auMatch.name;
  }

  // Try US states
  if (lowerCountryCode === 'us' || !countryCode) {
    const usMatch = US_STATES.find(state => state.value.toLowerCase() === lowerStateCode);
    if (usMatch) return usMatch.name;
  }

  // If no match found, return original value
  return stateCode;
};

/**
 * Configuration for countries that use a state/region dropdown and have
 **/

const COUNTRY_STATE_CONFIG = [
  { alpha2: 'AU', divisionsKey: 'AU_STATES', stateRequired: true, countyRequired: false },
  { alpha2: 'US', divisionsKey: 'US_STATES', stateRequired: true, countyRequired: true },
];

export const resolvedStateConfig = COUNTRY_STATE_CONFIG.map(config => ({
  ...config,
  countryValue: countries.find(c => c.alpha2 === config.alpha2)?.value,
  states: adminDivisions[config.divisionsKey] ?? [],
})).filter(c => c.countryValue);

export const COUNTRIES_WITH_STATE_DROPDOWN = (resolvedStateConfig || []).map(c => c.countryValue);

export const COUNTRIES_WITH_REQUIRED_STATE = (resolvedStateConfig || [])
  .filter(c => c.stateRequired)
  .map(c => c.countryValue);

export const COUNTRIES_WITH_REQUIRED_COUNTY = (resolvedStateConfig || [])
  .filter(c => c.countyRequired)
  .map(c => c.countryValue);

export const getStateOptions = countryValue =>
  (resolvedStateConfig || []).find(c => c.countryValue === countryValue)?.states ?? [];

/**
 * Normalizes a stored region_state value (full name or code) to the option value (code)
 * used by the state Select. Google and manual entry often store full names (e.g. "California");
 * the dropdown options use codes (e.g. "CA"). Returns the code so the Select shows as selected.
 */
export const getStateOptionValue = (storedRegionState, countryValue) => {
  if (!storedRegionState) return storedRegionState;
  const options = getStateOptions(countryValue);
  if (!options.length) return storedRegionState;
  const byValue = options.find(
    s => (s.value || '').toLowerCase() === (storedRegionState || '').toLowerCase(),
  );
  if (byValue) return byValue.value;
  const byName = options.find(
    s => (s.name || '').toLowerCase() === (storedRegionState || '').toLowerCase(),
  );
  if (byName) return byName.value;
  return storedRegionState;
};
